import React, { ReactElement } from 'react';
import { SectionTitle } from '@components/common/section-title';
import { RoomTypesGroup } from './RoomTypesGroup';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { RoomBasket } from '@model/iceberg/booking';
import { ContentTabs } from '@components/common/content-tabs';
import { RoomTypeComponentVariant } from '@model/fit-product';
import { Room } from '@model/iceberg/deal-finder/deal-finder';
import { RoomTypeGroupSkeleton } from '@components/product/room-types/RoomTypesGroupSkeleton';

/* ***************** *
 *       Types       *
 * ***************** */
export interface RoomTypeTabsProps {
  theme?: Theme;
  testId?: string;
  rooms: Array<Array<Room>>;
  heading?: string;
  subheading?: string | ReactElement;
  onSelectBoardBasis?: (token: string, sequence: number) => void;
  selectedRooms: Array<RoomBasket>;
  isGhosting?: boolean;
  onScrolledIntoView?: () => void;
  isTour?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div({});

const StyledSectionTitle = styled(SectionTitle)(({ theme }: ThemeProps) => ({
  marginBottom: theme.custom.spacing.small
}));

const SectionSubtitle = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  marginBottom: theme.custom.spacing.medium
}));

/* *************************** *
 *    RoomTypeTabsComponent    *
 * *************************** */
export const RoomTypeTabsComponent = ({
  rooms,
  heading,
  subheading,
  onSelectBoardBasis,
  selectedRooms,
  testId,
  isGhosting,
  onScrolledIntoView,
  isTour
}: RoomTypeTabsProps) => {
  if (isGhosting) {
    return (
      <Container data-testid={testId || TestId.roomTypeTabs.main}>
        {heading && <SectionTitle heading={heading} />}
        <RoomTypeGroupSkeleton />
      </Container>
    );
  }

  const getRoomTabLabels: () => Array<string> = () => [...Array(rooms.length)].map((_, index) => `Room ${index + 1}`);
  const renderRoomTypeGroups = () =>
    rooms.map((roomTypes: Array<Room>, index) => {
      const sequence: number = index + 1;
      const selected: RoomBasket | undefined = selectedRooms.find((room: RoomBasket) => room.sequence === sequence);
      if (selected) {
        return (
          <RoomTypesGroup
            key={index}
            roomTypes={roomTypes}
            onSelectBoardBasis={onSelectBoardBasis}
            selectedRoom={selected}
            isLoading={isGhosting}
            variant={RoomTypeComponentVariant.BOOKING_BED_BANKS}
            onScrolledIntoView={onScrolledIntoView}
            isTour={isTour}
          />
        );
      }
      return null;
    });

  return (
    <Container data-testid={testId || TestId.roomTypeTabs.main}>
      {heading && <StyledSectionTitle heading={heading} />}
      {subheading && <SectionSubtitle>{subheading}</SectionSubtitle>}
      <ContentTabs tabLabels={getRoomTabLabels()}>{renderRoomTypeGroups()}</ContentTabs>
    </Container>
  );
};

export const RoomTypeTabs = withTheme(RoomTypeTabsComponent);
